import React, { useContext, useEffect, useRef, useState } from 'react';
import { ELSInlineLoader } from '@els/els-ui-common-react';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import { GeneratedQuizQuestionDto } from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { AppContext } from '../app/AppProvider.component';
import { fetchGeneratedQuizQuestionsAction, trackNewRelicAction } from '../app/app-state-actions.utilities';
import {
  Application,
  AppAction,
} from '../../apis/eols-app-link/eols-app-link.constants';
import { AiChatAssessmentPlayerOutBody } from '../../apis/eols-app-link/eols-app-link.dtos';
import { getExternalAppRedirectUrl } from '../../apis/eols-app-link/eols-app-link.utilities';
import { AppLinkOutConfig } from '../../apis/eols-app-link/eols-app-link.models';
import { getParentLinkId } from '../../utilities/app.utilities';
import { ServerConstants } from '../app/server.constants';
import { AssessmentPlayerIframeEvent } from '../../pages/ai-chat/ai-chat.constants';
import { isAllowedMessageOrigin } from '../../pages/ai-chat/ai-chat.utilities';
import { FlexLayout } from '../flex/FlexLayout.component';
import { FlexItem } from '../flex/FlexItem.component';
import { FlexLayoutModifier } from '../flex/flex.constants';
import { NewRelicAnalyticsAction, QuizTakeFailureStage } from '../../models/new-relic-analytics.models';
import { copyContentToClipboard, formatQuizQuestionsForCopy } from '../../utilities/common.utilities';
import { BASE_TOAST_CONFIG } from '../../constants/toast.constants';
import IconWithText from '../icon-with-text/IconWithText.component';
import { ELSButtonIconPosition } from '../../models/button.models';
import { QuizPanelMode } from './quiz-questions-panel.constants';
import QuizQuestionList from './QuizQuestionList.component';

export type QuizQuestionsPanelPropsOnly = {
  botEntryId: number;
  mode: QuizPanelMode;
  onComplete: () => void;
};

export type QuizQuestionsPanelProps = QuizQuestionsPanelPropsOnly & ELSPropsFromToastService;

type QuizQuestionsPanelState = {
  quizQuestions: GeneratedQuizQuestionDto[];
  quizPlayerUrl: string;
  assessmentId: number;
  isLoading: boolean;
  errorMessage: string;
};

const defaultState: QuizQuestionsPanelState = {
  quizQuestions: null,
  quizPlayerUrl: null,
  assessmentId: null,
  isLoading: false,
  errorMessage: null,
};

export const QuizQuestionsPanelComponent = (props: QuizQuestionsPanelProps): JSX.Element => {

  const {
    appState,
    appStateRef,
    setAppState
  } = useContext(AppContext);

  const [state, setState] = useState<QuizQuestionsPanelState>(defaultState);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Wall-clock start of the current TAKE-mode setup attempt, so every outcome
  // event can report how long the user waited before it resolved.
  const takeStartTimeRef = useRef<number>(null);

  // Resolve the mode once so the fetch and render branches can never disagree
  // about what a missing mode means and leave the drawer blank.
  const mode = props.mode ?? QuizPanelMode.VIEW;

  const handleCopyClick = () => {
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_COPY_CLICK,
        props: {
          chatUserRole: appState.chatUserRole
        }
      }
    });

    const formattedQuestions = formatQuizQuestionsForCopy(state.quizQuestions);

    copyContentToClipboard(formattedQuestions);

    props.toastService.openToast({
      ...BASE_TOAST_CONFIG,
      component: (
        <div>Successfully copied all quiz questions</div>
      ),
      type: ELSWithToastService.types.POSITIVE
    });
  };

  const renderCopyFooter = (): JSX.Element => (
    <div className="c-naa-panel__quiz-footer u-els-margin-top">
      <FlexLayout modifiers={[
        FlexLayoutModifier.GUTTERS,
        FlexLayoutModifier.MIDDLE,
        FlexLayoutModifier.LEFT,
      ]}>
        <FlexItem>
          {/*
            The label is visible rather than screen-reader-only: users were
            missing the icon-only control. The visible text is the accessible
            name, so there is deliberately no aria-label to announce twice.
          */}
          <button
            onClick={handleCopyClick}
            type="button"
            className="u-els-debuttonize u-els-color-secondary u-els-display-block u-els-margin-top-1o8"
          >
            <IconWithText
              iconName="duplicate-document"
              iconPrefix="gizmo"
              iconPosition={ELSButtonIconPosition.LEFT}
            >
              Copy all questions
            </IconWithText>
          </button>
        </FlexItem>
      </FlexLayout>
    </div>
  );

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      quizQuestions: null,
      quizPlayerUrl: null,
      assessmentId: null,
      errorMessage: null,
    }));

    const { nacsBaseUrl } = ServerConstants[ServerConstants.DataSource];

    takeStartTimeRef.current = Date.now();

    // Tracks which leg of the chain below is in flight so the shared catch can
    // attribute the failure. Both legs surface the same message to the user.
    let failureStage = QuizTakeFailureStage.FETCH_QUESTIONS;

    if (mode === QuizPanelMode.TAKE) {
      trackNewRelicAction({
        appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_TAKE_OPEN,
          props: {
            entryId: props.botEntryId,
            chatUserRole: appState.chatUserRole,
          }
        }
      });
    }

    fetchGeneratedQuizQuestionsAction({
      appState,
      appStateRef,
      setAppState,
      botEntryId: props.botEntryId,
    })
      .then((questions) => {
        if (mode !== QuizPanelMode.TAKE) {
          setState((prevState) => ({
            ...prevState,
            quizQuestions: questions,
            isLoading: false,
            errorMessage: questions?.length ? null : 'No quiz questions found',
          }));
          return Promise.resolve();
        }

        const assessmentId = questions?.[0]?.eolsAssessmentId;
        if (!assessmentId) {
          trackNewRelicAction({
            appStateRef,
            actionProps: {
              action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_TAKE_ERROR,
              props: {
                entryId: props.botEntryId,
                chatUserRole: appState.chatUserRole,
                stage: QuizTakeFailureStage.MISSING_ASSESSMENT_ID,
                errorMessage: 'No eolsAssessmentId on the first generated quiz question',
                durationMs: Date.now() - takeStartTimeRef.current,
                questionCount: questions?.length || 0,
              }
            }
          });
          setState((prevState) => ({
            ...prevState,
            isLoading: false,
            errorMessage: 'No quiz questions found',
          }));
          return Promise.resolve();
        }

        const outBody: AiChatAssessmentPlayerOutBody = {
          eolsUserId: Number(appState.userId),
          assessmentId,
          getNextQuestionAPI: `${nacsBaseUrl}/nacs/assessment/next-question?assessmentId=${assessmentId}`,
          submitQuestionAPI: `${nacsBaseUrl}/nacs/assessment/submit-question`,
          assessmentPlayerConfig: {
            navigationType: 'Submit-Continue',
            strictStudyMode: true,
            hideSubmissionConfirmationPage: true,
            showReportAtTheEndOfAssignment: false,
            hideSubmitModal: true,
            exitToSourceButton: false,
            notifyParentOnSessionEnd: true,
          },
          integrationMetadata: {
            name: 'Nora AI Quiz',
          },
          terminology: {
            assessmentEntityName: 'quiz',
          },
        };

        const config: AppLinkOutConfig = {
          action: AppAction.ASSESSMENT_START,
          app: Application.NHE_ASSESSMENT_PLAYER,
          body: outBody,
          altSrcApp: Application.NHE_AI,
          includeLinkHash: true,
          parentLinkId: getParentLinkId(appState.appLinkData, appState.appLinkCookies),
        };

        failureStage = QuizTakeFailureStage.APP_LINK_OUT;

        return getExternalAppRedirectUrl(config)
          .then((response) => {
            trackNewRelicAction({
              appStateRef,
              actionProps: {
                action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_TAKE_READY,
                props: {
                  entryId: props.botEntryId,
                  chatUserRole: appState.chatUserRole,
                  assessmentId,
                  questionCount: questions?.length || 0,
                  durationMs: Date.now() - takeStartTimeRef.current,
                }
              }
            });
            setState((prevState) => ({
              ...prevState,
              quizQuestions: questions,
              quizPlayerUrl: response.redirectUrl,
              assessmentId,
              isLoading: false,
            }));
          });
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to load quiz questions';
        if (mode === QuizPanelMode.TAKE) {
          const responseStatus = error?.response?.status;
          trackNewRelicAction({
            appStateRef,
            actionProps: {
              action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_TAKE_ERROR,
              props: {
                entryId: props.botEntryId,
                chatUserRole: appState.chatUserRole,
                stage: failureStage,
                errorMessage: error?.message || errorMessage,
                durationMs: Date.now() - takeStartTimeRef.current,
                ...(responseStatus !== undefined && responseStatus !== null
                  ? { status: String(responseStatus) }
                  : {}),
              }
            }
          });
        }
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          errorMessage,
        }));
      });
  }, [props.botEntryId, mode]);

  useEffect(() => {
    if (mode !== QuizPanelMode.TAKE || !props.onComplete) {
      return undefined;
    }

    const handleMessage = (e: MessageEvent) => {
      if (!isAllowedMessageOrigin(e.origin)) {
        return;
      }
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow) {
        return;
      }
      if (e.data?.type === AssessmentPlayerIframeEvent.ASSESSMENT_PLAYER_COMPLETE) {
        props.onComplete();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [props.onComplete, mode]);

  if (state.isLoading) {
    return (
      <div className="u-els-padding">
        <ELSInlineLoader />
        <div className="u-els-margin-top">Preparing your quiz questions… this may take a few seconds.</div>
      </div>
    );
  }

  if (state.errorMessage) {
    return (
      <div className="u-els-padding">
        <div className="u-els-color-warn">{state.errorMessage}</div>
      </div>
    );
  }

  if (mode === QuizPanelMode.VIEW && state.quizQuestions) {
    return (
      <>
        <QuizQuestionList questions={state.quizQuestions} />
        {renderCopyFooter()}
      </>
    );
  }

  if (state.quizPlayerUrl) {
    return (
      <>
        <iframe
          ref={iframeRef}
          src={state.quizPlayerUrl}
          className="c-naa-panel__quiz-iframe"
          title="Quiz Player"
          allowFullScreen
        />
        {renderCopyFooter()}
      </>
    );
  }

  return null;
};

export const QuizQuestionsPanel: React.ComponentType<QuizQuestionsPanelPropsOnly> = ELSWithToastService(QuizQuestionsPanelComponent);

export default QuizQuestionsPanel;
